
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { pageRoute } from "../utils/common";
import {
  Send,
  ShieldAlert,
  Trophy,
  Globe,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
  MessageCircle,
} from "lucide-react";

import {
  FaDiscord,
  FaPinterestP,
  FaRedditAlien,
  FaSnapchatGhost,
} from "react-icons/fa";

import { SiTiktok } from "react-icons/si";

export default function Footer() {
  const tournaments = useSelector((state) => state.tournaments) || [];
  const webSiteData = useSelector((state) => state.website) || {};

  // =====================================
  // SOCIAL MEDIA LIST
  // =====================================
  const socialLinks = [
    {
      link: webSiteData?.facebookLink,
      icon: <Facebook size={18} />,
      bg: "bg-blue-600",
    },

    {
      link: webSiteData?.instagramLink,
      icon: <Instagram size={18} />,
      bg: "bg-pink-500",
    },

    {
      link: webSiteData?.telegramLink,
      icon: <Send size={18} />,
      bg: "bg-sky-500",
    },

    {
      link: webSiteData?.twitterLink,
      icon: <Twitter size={18} />,
      bg: "bg-cyan-500",
    },

    {
      link: webSiteData?.youtubeLink,
      icon: <Youtube size={18} />,
      bg: "bg-red-600",
    },

    {
      link: webSiteData?.linkedinLink,
      icon: <Linkedin size={18} />,
      bg: "bg-blue-700",
    },

    {
      link: webSiteData?.discordLink,
      icon: <FaDiscord size={18} />,
      bg: "bg-indigo-600",
    },

    {
      link: webSiteData?.snapchatLink,
      icon: <FaSnapchatGhost size={18} />,
      bg: "bg-yellow-500 text-black",
    },

    {
      link: webSiteData?.pinterestLink,
      icon: <FaPinterestP size={18} />,
      bg: "bg-rose-600",
    },

    {
      link: webSiteData?.redditLink,
      icon: <FaRedditAlien size={18} />,
      bg: "bg-orange-500",
    },
  ];

  return (
    <footer className="bg-[#f5f5f5] dark:bg-[#111111] border-t border-gray-200 dark:border-gray-800 transition-all duration-300">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14">
        {/* TOP SECTION */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* LOGO SECTION */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <img
                src="https://footplayhd.com/logo.png"
                alt="logo"
                className="w-10 h-10"
              />

              <div>
                <h2 className="text-2xl font-bold text-purple-600">
                  FootPlayHD.com
                </h2>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-400 leading-7 text-[15px] max-w-[300px]">
              Watch live sports matches online. Get live scores,
              schedules and streaming links.
            </p>
            {/* ===================================== */}
            {/* APP BUTTONS */}
            {/* ===================================== */}

            <div className="flex flex-wrap gap-3 mt-8">

              {webSiteData?.androidAppLink && (
                <a
                  href={
                    webSiteData?.androidAppLink
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://footplayhd.com/play-store.png"
                    alt="play-store"
                    className="h-10"
                  />
                </a>
              )}

              {webSiteData?.iosAppLink && (
                <a
                  href={
                    webSiteData?.iosAppLink
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://footplayhd.com/app-store.png"
                    alt="app-store"
                    className="h-10"
                  />
                </a>
              )}

            </div>
            <div className="flex items-center gap-4 mt-6 flex-wrap">

              {socialLinks?.filter((item) => item?.link)?.map(
                (item, index) => {

                  if (!item?.link)
                    return null;

                  return (
                    <a
                      key={index}
                      href={item?.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-full ${item?.bg} text-white flex items-center justify-center hover:scale-110 transition-all duration-300 shadow-lg`}
                    >
                      {item?.icon}
                    </a>
                  );
                }
              )}

            </div>
          </div>

          {/* QUICK LINKS */}
          <div>
            <h3 className="text-xl font-semibold text-black dark:text-white mb-6">
              Quick Links
            </h3>

            <div className="flex flex-col gap-4">
              {[
                {
                  name: "Home",
                  path: pageRoute.home,
                },
                // {
                //   name: "Matches",
                //   path: "/matches",
                // },
                {
                  name: "DMCA",
                  path: pageRoute.dmca,
                },
                {
                  name: "Contact Us",
                  path: pageRoute.contactUs,
                },
                {
                  name: "Blogs",
                  path: pageRoute.blogs,
                },
              ].map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className="text-gray-700 dark:text-gray-400 hover:text-orange-500 transition-all duration-300"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* TOURNAMENTS */}
          <div>
            <h3 className="flex items-center gap-2 text-xl font-semibold text-black dark:text-white mb-6">
              <Trophy size={20} />

              Tournaments
            </h3>

            <div className="space-y-6">
              {/* FOOTBALL */}
              <div>
                <h4 className="flex items-center gap-2 uppercase text-sm tracking-[2px] text-gray-600 dark:text-gray-500 mb-4">
                  ⚽ Football
                </h4>

                <div className="flex flex-col gap-3">
                  {tournaments?.slice(0, 5)?.map((t, index) => (
                    <Link
                      key={index}
                      to={"/tournament/" + t.league_id}
                      className="flex items-center gap-2 text-gray-700 dark:text-gray-400 hover:text-orange-500 transition-all"
                    >
                      <Globe size={14} />
                      {t.league_name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* CRICKET */}
              {/* <div>
                <h4 className="flex items-center gap-2 uppercase text-sm tracking-[2px] text-gray-600 dark:text-gray-500 mb-4">
                  🏏 Cricket
                </h4>

                <div className="flex flex-col gap-3">
                  {[
                    "Indian Premier League",
                    "New Zealand tour of Bangladesh",
                    "Pakistan tour of Bangladesh",
                  ].map((item, index) => (
                    <Link
                      key={index}
                      to="/"
                      className="flex items-center gap-2 text-gray-700 dark:text-gray-400 hover:text-orange-500 transition-all"
                    >
                      <Globe size={14} />

                      {item}
                    </Link>
                  ))}
                </div>
              </div> */}
            </div>
          </div>

          {/* LEGAL */}
          <div>
            <h3 className="text-xl font-semibold text-black dark:text-white mb-6">
              Legal
            </h3>

            <div className="flex flex-col gap-4">
              {[
                {
                  name: "DMCA",
                  path: pageRoute.dmca,
                },
                {
                  name: "Contact Us",
                  path: pageRoute.contactUs,
                },
              ].map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className="text-gray-700 dark:text-gray-400 hover:text-orange-500 transition-all duration-300"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* BORDER */}
        <div className="border-t border-gray-300 dark:border-gray-800 my-12"></div>

        {/* DISCLAIMER */}
        {webSiteData?.importantDisclaimer &&
          <div className="border border-orange-300 dark:border-orange-700 rounded-[25px] p-8 md:p-10 bg-[#fffaf7] dark:bg-[#1a1410] text-center">
            <div className="flex items-center justify-center gap-2 text-orange-500 mb-5">
              <ShieldAlert size={24} />

              <h2 className="text-xl md:text-2xl font-bold uppercase">
                Important Disclaimer
              </h2>
            </div>

            <p className="text-gray-700 dark:text-gray-400 leading-8 max-w-[900px] mx-auto text-[15px] md:text-[16px]">
              {webSiteData?.importantDisclaimer}
            </p>
          </div>
        }

        {/* COPYRIGHT */}
        <div className="pt-10 text-center">
          <p className="text-gray-700 dark:text-gray-500 text-sm md:text-base">
            © Copyright 2026 FootPlayHD.com | All Right Reserved. | Developed with ❤️ by{" "}
            <strong>
              <a
                href="https://ogwebguru.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1f73f1" }}
                className="text-primary hover:underline"
              >
                OG Web Guru
              </a>
            </strong>
          </p>
        </div>
        <div
          className="md:hidden">
          <br />
          <br />
        </div>
      </div>

      {/* FLOATING TELEGRAM BUTTON */}
      {/* <div className="fixed bottom-5 left-5 z-50">
        <button className="flex items-center gap-2 bg-[#2AABEE] hover:scale-105 transition-all duration-300 text-white px-5 py-4 rounded-full shadow-2xl">
          <Send size={18} />

          Join Now
        </button>
      </div> */}

      {/* FLOATING CHAT BUTTON */}
      {/* <div className="fixed bottom-5 right-5 z-50">
        <button className="w-[65px] h-[65px] rounded-full bg-red-500 hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-2xl border-4 border-white dark:border-[#222]">
          <div className="relative">
            <div className="w-7 h-7 border-[3px] border-white rounded-full"></div>

            <span className="absolute -top-1 -right-1 w-3 h-3 bg-pink-400 rounded-full"></span>
          </div>
        </button>
      </div> */}
    </footer>
  );
}